import Joi from 'joi';
import { RestructuredResponseItemSchema } from '../hooks/useSextforceAutoCampaignTipReply';
import { MomentTimePeriodSchema } from '../hooks/useSubscriber';
import { OnlyFansVaultMedias } from '../hooks/useSubscriberVaultAlbums';
import { arrayOnlyStrugglesTtsProviders, OnlyStrugglesTtsProviders } from '../hooks/useTtsReferenceModels';
import { OnlyFansLists } from './onlyFansTypes';

export interface OnlyFansSubscriberMessageUsersAutoMessageDetailsDelayBeforeSendingMessage {
    fromSeconds: number;
    toSeconds: number;
}

export interface OnlyFansSubscriberMessageUsersAutoMessageDetailsTaggedCreator {
    id: number;
    name: string;
}

export interface OnlyFansSubscriberMessageUsersAutoMessageDetailsTaggedReleaseForm {
    id: number;
    name: string;
}

export interface MomentTimePeriod {
    amount: number;
    unit: string;
}

export interface OnlyFansListIdAndName {
    id: number | string | OnlyFansLists.Type;
    name: string;
}

export interface VoiceSettings {
    /** Determines how stable the voice is and the randomness between each generation. Lower values introduce broader emotional range for the voice. Higher values can result in a monotonous voice with limited emotion. */
    stability?: number;
    /** This setting boosts the similarity to the original speaker. Using this setting requires a slightly higher computational load, which in turn increases latency. */
    useSpeakerBoost?: boolean;
    /** Determines how closely the AI should adhere to the original voice when attempting to replicate it. */
    similarityBoost?: number;
    /** Determines the style exaggeration of the voice. This setting attempts to amplify the style of the original speaker. It does consume additional computational resources and might increase latency if set to anything other than 0. */
    style?: number;
    /** Adjusts the speed of the voice. A value of 1.0 is the default speed, while values less than 1.0 slow down the speech, and values greater than 1.0 speed it up. */
    speed?: number;
}

export interface OnlyFansSubscriberMessageUsersTtsSettings {
    active: boolean;
    message: string | null;
    provider: OnlyStrugglesTtsProviders | null;
    modelId: string | null;
    addUsername: boolean;
    usernameGreeting?: string | null;
    generatedFilename?: {
        url: string;
        fileName: string;
    } | null;
    voiceSettings?: VoiceSettings | null;
}

export const OnlyFansSubscriberMessageUsersTtsSettingsVoiceSettingsSchema = Joi.object<VoiceSettings>({
    stability: Joi.number().min(0).max(1).optional(),
    useSpeakerBoost: Joi.boolean().optional(),
    similarityBoost: Joi.number().min(0).max(1).optional(),
    style: Joi.number().min(0).max(1).optional(),
    speed: Joi.number().min(0.5).max(2).optional(),
}).optional();

export const OnlyFansSubscriberMessageUsersTtsSettingsSchema = Joi.object({
    active: Joi.boolean().required(),
    message: Joi.string().allow(null).required(),
    provider: Joi.string()
        .allow(null)
        .valid(...arrayOnlyStrugglesTtsProviders)
        .required(),
    modelId: Joi.string().allow(null).required(),
    addUsername: Joi.boolean().required(),
    usernameGreeting: Joi.string().allow(null),
    generatedFilename: Joi.object({
        url: Joi.string().required(),
        fileName: Joi.string().required(),
    }).allow(null),
    voiceSettings: OnlyFansSubscriberMessageUsersTtsSettingsVoiceSettingsSchema.allow(null).optional(),
}).required();

export const createEmptyOnlyFansSubscriberMessageUsersTtsSettings = (
    active?: boolean,
    message?: string,
    provider?: OnlyStrugglesTtsProviders,
    modelId?: string,
    addUsername?: boolean,
    usernameGreeting?: string,
    generatedFilename?: {
        url: string;
        fileName: string;
    },
    voiceSettings?: VoiceSettings | null,
): OnlyFansSubscriberMessageUsersTtsSettings => {
    return {
        active: active || false,
        message: message || null,
        provider: provider || null,
        modelId: modelId || null,
        addUsername: addUsername || false,
        usernameGreeting: usernameGreeting || null,
        generatedFilename: generatedFilename || null,
        voiceSettings: voiceSettings || null,
    };
};

export interface OnlyFansSubscriberMessageUsersAutoMessageDetails {
    active: boolean;
    delayBeforeSendingMessage: OnlyFansSubscriberMessageUsersAutoMessageDetailsDelayBeforeSendingMessage | null;
    lockMessagePrice: number | null;
    lockedText: boolean;
    message: string;
    replaceUsername: string | null;
    mediaFiles: OnlyFansVaultMedias.RestructuredResponseItem[];
    mediaFilesPreviews: number[];
    taggedCreators: OnlyFansSubscriberMessageUsersAutoMessageDetailsTaggedCreator[];
    taggedReleaseForms?: OnlyFansSubscriberMessageUsersAutoMessageDetailsTaggedReleaseForm[];
    autoUnsendTimerPeriod: MomentTimePeriod | null;
    delayBetweenMessages: number | null;
    excludeLists: OnlyFansListIdAndName[] | null;
    excludeSpentCondition: string | null;
    excludeSpentAmount: number | null;
    excludeSpentPeriod: MomentTimePeriod | null;
    excludeMessagedInPeriod: MomentTimePeriod | null;
    skipFansActive: boolean;
    skipFollowingActive: boolean;
    skipExpiredFans: boolean;
    addToList?: OnlyFansListIdAndName | null;
    tts?: OnlyFansSubscriberMessageUsersTtsSettings | null;
}

export const OnlyFansSubscriberMessageUsersAutoMessageDetailsSchema = Joi.object<OnlyFansSubscriberMessageUsersAutoMessageDetails>({
    active: Joi.boolean().required(),
    delayBeforeSendingMessage: Joi.object<OnlyFansSubscriberMessageUsersAutoMessageDetailsDelayBeforeSendingMessage>({
        fromSeconds: Joi.number().required(),
        toSeconds: Joi.number().required(),
    }).allow(null),
    lockMessagePrice: Joi.number().allow(null),
    lockedText: Joi.boolean().allow(null).default(false),
    message: Joi.string().trim().allow('').default(''),
    replaceUsername: Joi.string().allow(null, '').default(null),
    mediaFiles: Joi.array().items(RestructuredResponseItemSchema).default([]),
    mediaFilesPreviews: Joi.array().items(Joi.number()).default([]),
    taggedCreators: Joi.array().items(
        Joi.object<OnlyFansSubscriberMessageUsersAutoMessageDetailsTaggedCreator>({
            id: Joi.number().required(),
            name: Joi.string().required(),
        }).default([]),
    ),
    taggedReleaseForms: Joi.array().items(
        Joi.object<OnlyFansSubscriberMessageUsersAutoMessageDetailsTaggedReleaseForm>({
            id: Joi.number().required(),
            name: Joi.string().required(),
        }).default([]),
    ),
    autoUnsendTimerPeriod: MomentTimePeriodSchema.allow(null),
    delayBetweenMessages: Joi.number().allow(null),
    excludeLists: Joi.array()
        .items(Joi.object<OnlyFansListIdAndName>({ id: Joi.number().required(), name: Joi.string().required() }))
        .allow(null),
    excludeSpentCondition: Joi.string().allow(null),
    excludeSpentAmount: Joi.number().allow(null),
    excludeSpentPeriod: MomentTimePeriodSchema.allow(null),
    excludeMessagedInPeriod: MomentTimePeriodSchema.allow(null),
    skipFansActive: Joi.boolean().required(),
    skipFollowingActive: Joi.boolean().required(),
    skipExpiredFans: Joi.boolean().required(),
    addToList: Joi.object<OnlyFansListIdAndName>({ id: Joi.number().required(), name: Joi.string().required() }).allow(null).optional(),
    tts: OnlyFansSubscriberMessageUsersTtsSettingsSchema.allow(null).optional(),
});

export const createEmptyOnlyFansSubscriberMessageUsersAutoMessageDetails = (
    active?: boolean,
): OnlyFansSubscriberMessageUsersAutoMessageDetails => {
    return {
        active: active || false,
        delayBeforeSendingMessage: null,
        lockMessagePrice: null,
        lockedText: false,
        message: '',
        replaceUsername: null,
        mediaFiles: [],
        mediaFilesPreviews: [],
        taggedCreators: [],
        taggedReleaseForms: [],
        autoUnsendTimerPeriod: null,
        delayBetweenMessages: null,
        excludeLists: null,
        excludeSpentCondition: null,
        excludeSpentAmount: null,
        excludeSpentPeriod: null,
        excludeMessagedInPeriod: null,
        skipFansActive: false,
        skipFollowingActive: false,
        skipExpiredFans: false,
        addToList: null,
        tts: null,
    };
};
